import axios, { Canceler } from 'axios';
import { LOCALE_KEY } from '@fx-ui/intl';
import {
    get,
    redirect,
    ECode,
    compact,
    URL_PARAMETER,
    DeptType,
    MemberType,
    map,
    filter,
    UserTenant,
    SuiteId,
    UserTenantsFilterResult,
    MemberFieldApiData,
    omit,
    getInvitationMemberTypeParams
} from '@libs/shared';
import { getRequestUrl, post } from '../utils';
import {
    CurrentMember,
    DeptCardView,
    DeptStatus,
    IDept,
    ContactRoleInfo,
    IMember,
    LoginUserDept,
    LoginInfo,
    MemberCardView,
    MemberStatus,
    PlatformVipAddOnKey,
    RoleCardView,
    RoleOrGroupFilterType,
    VerifyProfile,
    CoinBalance,
    CorpGroupConfig
} from '../interfaces';
import { API } from '../constants';
import { authModel, globalState } from '../models';
import siteRouterManager, { SiteRouterKey } from '../utils/site-router';

export const getCsrf = () => {
    return axios.post(getRequestUrl(API.common.get_csrf), null, {
        withCredentials: true
    });
};

export const getGlobalVariables = () => {
    return post(getRequestUrl(API.common.get_public_configuration));
};

export const fetchLoginInfo = (suiteId?: number) => {
    const customHandleHttpCodes = [401];

    return post<LoginInfo>(
        getRequestUrl(API.authentication.get_login_user),
        {
            suite_id: suiteId
        },
        {
            customHandleHttpCodes
        }
    ).then((userRes) => {
        const tenantId = userRes.data?.tenant?.id;

        if (!tenantId) {
            return userRes.data;
        }

        return post<CurrentMember>(getRequestUrl(API.authz.get_permissions, tenantId), undefined, {
            customHandleHttpCodes
        }).then((authRes) => {
            return {
                ...userRes.data,
                member: {
                    ...userRes.data.member,
                    ...authRes.data
                }
            };
        });
    });
};

export const logout = (redirectUrl?: string) => {
    redirect(
        siteRouterManager.getQueryPath(
            compact([
                redirectUrl && {
                    key: URL_PARAMETER.REDIRECT_URI,
                    value: redirectUrl
                }
            ]),
            SiteRouterKey.SignOut
        )
    );
};

/**
 * 获取用户所处的所有企业
 */
export const getUserTenants = () => {
    return new Promise<UserTenant[]>((resolve, reject) => {
        post<{ corps: UserTenant[] }>(getRequestUrl(API.authentication.get_tenant_list))
            .then((res) => {
                const corps = res.data.corps ?? [];
                const tenantIds = map(corps, 'tenant_id');

                // 租户过滤 & 拼接非平台租户
                const handleFilter = (
                    filteredTenantIds: string[],
                    nonPlatformTenants: UserTenantsFilterResult['non_platform_tenants'] = []
                ) => {
                    const filteredTenantIdSet = new Set(filteredTenantIds);
                    const filteredTenants = filter(corps, ({ tenant_id }) =>
                        filteredTenantIdSet.has(tenant_id)
                    );
                    resolve([
                        ...filteredTenants,
                        ...map(nonPlatformTenants, (item) => ({
                            ...item,
                            isCorpOwner: item.is_corp_owner,
                            isNotMigrated: true
                        }))
                    ]);
                };

                if (globalState.userTenantsFilter) {
                    Promise.resolve(
                        globalState.userTenantsFilter({
                            tenant_ids: tenantIds,
                            jsy_user_id: authModel.jsyUserId
                        })
                    )
                        .then(({ tenant_ids, non_platform_tenants }) => {
                            handleFilter(tenant_ids, non_platform_tenants);
                        })
                        .catch(reject);
                } else {
                    resolve(corps);
                }
            })
            .catch(reject);
    });
};

/**
 * 获取当前登录用户所在部门列表
 * @param tenantId 企业 Id
 */
export const getLoginUserDeptList = (tenantId: string): Promise<LoginUserDept[]> => {
    return post<{ dept_list: LoginUserDept[] }>(
        getRequestUrl(API.corp.login_user_dept, tenantId)
    ).then((res) => res.data.dept_list);
};

export const switchCorp = (tenantId: string) => {
    return post(
        getRequestUrl(API.authentication.switch_corp),
        { target: tenantId },
        {
            customHandleErrorCodes: [ECode.USER_MFA_REQUIRED, ECode.USER_PHONE_UNBIND_BEFORE_MFA]
        }
    );
};

export const getImgCaptcha = () => {
    return post(`${getRequestUrl(API.authentication.generate_captcha)}?_=${new Date().getTime()}`);
};

export const getPhone = (params: any) => {
    return post(getRequestUrl(API.authentication.send_sms), params, {
        customHandleErrorCodes: [ECode.USER_NO_AUTH_CODE_CAPTCHA]
    });
};

export const getEmail = (params: any) => {
    return post(getRequestUrl(API.authentication.send_email), params, {
        customHandleErrorCodes: [ECode.USER_NO_AUTH_CODE_CAPTCHA]
    });
};

export const bindPhone = (params: { phone: string; phone_code: string }) => {
    return post(getRequestUrl(API.authentication.bind_phone), params, {
        customHandleErrorCodes: [ECode.USER_IS_CANCELING]
    });
};

export const handleMfa = (phoneCode: string) => {
    return post(
        getRequestUrl(API.authentication.verify_phone),
        {
            phone_code: phoneCode
        },
        {
            customHandleErrorCodes: [ECode.USER_IS_CANCELING]
        }
    );
};

export const checkCaptcha = (params: { email?: string; email_code: string; type: string }) => {
    return post(getRequestUrl(API.authentication.verify_code), params);
};

export const checkPassword = (params: { confirmPassword: string }) => {
    return post(getRequestUrl(API.authentication.verify_password), params);
};

/**
 * 校验加入企业，显式传入的成员类型优先于邀请链接参数
 * @param params 加入企业参数
 * @param invitationUrl 邀请链接，默认读取当前页面
 */
export const joinCorpVerify = (params: any, invitationUrl?: string) => {
    return post(
        getRequestUrl(API.account.join_corp),
        { ...getInvitationMemberTypeParams(invitationUrl), ...params },
        {
            customHandleErrorCodes: [ECode.USER_MFA_REQUIRED, ECode.USER_PHONE_UNBIND_BEFORE_MFA]
        }
    );
};

/**
 * 查询邀请企业信息
 * @param joinCorpId 企业 id
 * @param invitationUrl 邀请链接，默认读取当前页面
 */
export const fetchCorpInfo = (joinCorpId: string, invitationUrl?: string) => {
    return post(
        getRequestUrl(API.account.corp_public_get),
        {
            corp_id: joinCorpId,
            ...getInvitationMemberTypeParams(invitationUrl)
        },
        {
            customHandleErrorCodes: [
                ECode.VALUE_NOT_VALID,
                ECode.CORP_NOT_FOUND,
                ECode.TENANT_NOT_FOUND
            ]
        }
    );
};

export const createCorp = (params?: { name?: string; persona?: { company_detail: any } }) => {
    return post(getRequestUrl(API.corp.create_tenant), params);
};

/**
 * 检查套件是否可访问
 * @param suiteId
 */
export const checkSuiteAccessible = (suiteId: number, options?: Parameters<typeof post>[2]) => {
    return post(getRequestUrl(API.suite.check_access, suiteId), {}, options);
};

/**
 * 查看成员信息卡片
 * @param memberId
 * @param tenantId
 * @param cancelExecutor
 */
export const getMemberCardDetail = (
    memberId: string,
    tenantId: string,
    cancelExecutor?: (c: Canceler) => void
): Promise<{ data: MemberCardView; fields: MemberFieldApiData[] }> => {
    return post<MemberCardView & { fields: MemberFieldApiData[] }>(
        getRequestUrl(API.corp.user_detail, tenantId),
        { member_id: memberId },
        {
            customHandleErrorCodes: [
                ECode.CORP_MEMBER_NOT_EXISTS,
                ECode.DEPT_NOT_FOUND,
                ECode.MEMBER_NOT_JOINED_TENANT,
                ECode.MEMBER_IS_NOT_ACTIVE
            ],
            cancelExecutor
        }
    ).then((res) => {
        return {
            data: omit(res.data, 'fields'),
            fields: res.data?.fields ?? []
        };
    });
};

/**
 * 查看部门信息卡片
 * @param deptId
 * @param tenantId
 */
export const getDeptCardDetail = (deptId: string, tenantId: string): Promise<DeptCardView> => {
    return post(
        getRequestUrl(API.corp.dept_detail, tenantId),
        { dept_id: deptId },
        {
            customHandleErrorCodes: [ECode.DEPT_NOT_FOUND]
        }
    ).then((res) => get(res, 'data', {}));
};

/**
 * 查看角色信息卡片
 * @param roleId
 * @param tenantId
 */
export const getRoleCardDetail = (roleId: string, tenantId: string): Promise<RoleCardView> => {
    return post(
        getRequestUrl(API.corp.role_detail, tenantId),
        { role_id: roleId },
        {
            // 1098 企业成员不存在
            // 1201 企业角色信息不存在
            customHandleErrorCodes: [ECode.CORP_MEMBER_NOT_EXISTS, ECode.ROLE_NOT_FOUND]
        }
    ).then((res) => get(res, 'data', {}));
};

export interface VersionInfo {
    usage: {
        corp_coop: number;
        corp_coop_docker: {
            corpIdAndCountList: {
                corpId: string;
                count: number;
            }[];
        };
    };
    free_pack?: { corp_coop_docker: number; corp_coop: number };
    platform_plan: {
        pack: Record<PlatformVipAddOnKey, number>;
        exceed_items?: PlatformVipAddOnKey[];
    };
}
/**
 * 获取平台版本信息
 */
export const getPlatformVip = () => {
    return post<VersionInfo>(getRequestUrl(API.usage.get, authModel.tenantId)).then(
        (res) => res.data
    );
};

/**
 * 获取互联组织用量
 */
export const getCorpCoopUsage = () => {
    return post<{
        host_docker_usage: { count: number; name: string }[];
    }>(getRequestUrl(API.usage.get_corp_coop_usage, authModel.tenantId)).then((res) => res.data);
};

/**
 * 获取语言列表
 */
export const getLocaleList = () => {
    return post<{ label: string; value: LOCALE_KEY }[]>(
        getRequestUrl(API.i18n.get_locale_list)
    ).then((res) => res.data);
};

/**
 * 设置用户语言
 * @param locale
 */
export const updateUserLocale = (locale: LOCALE_KEY) => {
    return post(getRequestUrl(API.account.update_locale), { locale });
};

/**
 * 获取部门
 * @param tenantId
 * @param args
 * @param args.keyword
 * @param args.limit
 * @param args.skip
 * @param args.hasCount
 * @param args.status_list 后端默认 [DeptStatus.active]
 * @param args.type_list 后端默认 [DeptType.internal]
 */
export const getContactDeptList = (
    tenantId: string,
    args?: {
        keyword?: string | string[];
        limit?: number;
        skip?: number;
        hasCount?: boolean;
        status_list?: DeptStatus[];
        type_list?: DeptType[];
    }
) => {
    return post<{
        count: number;
        departments: IDept[];
    }>(getRequestUrl(API.corp.department_list, tenantId), args).then((res) => res.data);
};

/**
 * 获取内部角色
 * @param tenantId
 * @param args
 * @param args.keyword
 * @param args.limit
 * @param args.skip
 * @param args.filter_type RoleOrGroupFilterType
 * @param args.hasCount
 */
export const getContactRoleList = (
    tenantId: string,
    args?: {
        keyword?: string | string[];
        limit?: number;
        skip?: number;
        filter_type?: RoleOrGroupFilterType;
        hasCount?: boolean;
    }
) => {
    return post<ContactRoleInfo>(getRequestUrl(API.corp.role_list, tenantId), args).then(
        (res) => res.data
    );
};

/**
 * 获取外部角色
 * @param tenantId
 * @param args
 * @param args.keyword
 * @param args.limit
 * @param args.skip
 * @param args.hasCount
 */
export const getExternalRoleList = (
    tenantId: string,
    args?: {
        keyword?: string | string[];
        limit?: number;
        skip?: number;
        hasCount?: boolean;
    }
) => {
    return post<ContactRoleInfo>(
        getRequestUrl(API.external.role.get_role_list, tenantId),
        args
    ).then((res) => res.data);
};

/**
 * 获取部门/角色下成员
 * @param tenantId
 * @param args
 * @param args.dept_no 根据部门筛选
 * @param args.role_no 根据角色筛选
 * @param args.skip
 * @param args.limit
 * @param args.keyword
 * @param args.status_list  后端默认 [MemberStatus.active]
 * @param args.hasCount
 * @param args.type_list 后端默认 [MemberType.internal]
 */
export const getContactMemberList = (
    tenantId: string,
    args?: {
        dept_no?: number | null;
        role_no?: number;
        skip?: number;
        limit?: number;
        keyword?: string | string[];
        status_list?: MemberStatus[];
        hasCount?: boolean;
        type_list?: MemberType[];
    }
) => {
    return post<{ count: number; members: IMember[] }>(
        getRequestUrl(API.corp.member_list, tenantId),
        args
    ).then((res) => res.data);
};

/**
 * 获取外部成员
 * @param tenantId
 * @param args
 * @param args.role_no 根据角色筛选
 * @param args.skip
 * @param args.limit
 * @param args.keyword
 * @param args.status_list 后端默认 [MemberStatus.active]
 * @param args.hasCount
 */
export const getExternalMemberList = (
    tenantId: string,
    args?: {
        role_no?: number;
        skip?: number;
        limit?: number;
        keyword?: string | string[];
        status_list?: MemberStatus[];
        hasCount?: boolean;
    }
) => {
    return post<{ count: number; members: IMember[] }>(
        getRequestUrl(API.external.member.list, tenantId),
        args
    ).then((res) => res.data);
};

/**
 * 获取未读消息
 * @param types 指定消息类型，如果不传则默认返回 all
 * @param suiteId
 * @example getUnreadMessage()  ===>  { all: true }
 * @example getUnreadMessage(["data_notify", "app_log"])  ===>  { data_notify: false, app_log: true }
 */
export const getUnreadMessage = <T extends string = never>(types?: T[], suiteId?: SuiteId) => {
    type ReturnValue = [T] extends [never] ? { all: boolean } : Record<T, boolean>;

    return post<ReturnValue>(getRequestUrl(API.message.get_has_unread), {
        types,
        ...(suiteId ? { suite_id: suiteId } : {})
    }).then((res) => res.data);
};

/**
 * 获取一条未读的运营消息
 */
export interface Notice {
    _id: string;
    /**
     * 消息主题.
     */
    subject?: string;
    /**
     * 消息内容.
     */
    content: string[];
    /**
     * 消息链接.
     */
    link?: string;
    /**
     * 消息链接文字.
     */
    link_text?: string;
    /**
     * 消息图片.
     */
    image?: string;
}
export const getNotice = () => {
    return post<{ notice?: Notice }>(getRequestUrl(API.message.get_notice)).then((res) => res.data);
};

/**
 * 设置消息为已读
 * @param msgId string | string[]
 */
export const setNoticeAsRead = (msgId: string | string[]) => {
    return post(getRequestUrl(API.message.set_as_read), {
        messages: Array.isArray(msgId) ? msgId : [msgId]
    });
};

/**
 * 获取认证信息
 * @param tenantId
 */
export const getVerifyProfile = (tenantId = authModel.tenantId) => {
    return post<VerifyProfile>(getRequestUrl(API.verify.status, tenantId)).then((res) => res.data);
};

/**
 * 获取云币余额
 * @param tenantId
 */
export const getCoinBalance = (tenantId = authModel.tenantId) => {
    return post<CoinBalance>(getRequestUrl(API.coin.get_coin_balance, tenantId)).then(
        (res) => res.data
    );
};

/** 获取sso令牌 */
export const getBridgeToken = () => {
    return post<{ token: string }>(getRequestUrl(API.account.get_bridge_token)).then(
        (res) => res.data.token
    );
};

/**
 * 更新个人账号信息
 */
export const updateUserInfo = (
    args: {
        user?: {
            password?: string;
            nickname?: string;
            phone?: string;
            email?: string;
        };
        auth?: {
            password?: string;
        };
    },
    config?: Parameters<typeof post>[2]
) => {
    return post(getRequestUrl(API.account.update_user), args, config);
};

/**
 * 获取登录统计数据概览
 */
interface LoginStatsQueryArgs {
    from?: string;
    to?: string;
    members?: string[];
    granularity?: string;
    has_count?: boolean;
    sort?: Record<string, number>;
    limit?: number;
    skip?: number;
}
export interface LoginReportInfo {
    dates: string[];
    login_stats_count: number[];
    login_stats_times: number[];
}
export const getLoginReportList = (
    tenantId: string,
    args: LoginStatsQueryArgs,
    config?: Parameters<typeof post>[2]
) => {
    return post<LoginReportInfo>(
        getRequestUrl(API.audit.login_report_list, tenantId),
        args,
        config
    ).then((res) => res.data);
};

export interface LoginSummary {
    member_count: number;
    monthly_login_member_count: number;
    monthly_login_member_count_change: number;
    monthly_login_member_rate: number;
}

export const getLoginSummary = (tenantId: string, config?: Parameters<typeof post>[2]) => {
    return post<LoginSummary>(getRequestUrl(API.audit.login_summary, tenantId), {}, config).then(
        (res) => res.data
    );
};

/**
 * 获取登录统计数据详情
 */
export interface LoginMetrics {
    count: number;
    data: {
        date: string;
        login_stats_times: number;
        member: IMember;
        member_id: string;
        nickname: string;
    }[];
}
export const getLoginMetrics = (
    tenantId: string,
    args: LoginStatsQueryArgs,
    config?: Parameters<typeof post>[2]
) => {
    return post<LoginMetrics>(getRequestUrl(API.audit.login_metrics, tenantId), args, config).then(
        (res) => res.data
    );
};

/** 平台管理组-更新 */
export const updateManageGroup = (
    tenantId: string,
    args: {
        group_id: string;
        name?: string;
        op?: Partial<CorpGroupConfig['op']>;
        members?: string[];
        dept_range_type?: number;
        dept_range?: string[];
        role_range_type?: number;
        role_range?: string[];
    },
    config?: Parameters<typeof post>[2]
) => {
    return post<CorpGroupConfig>(
        getRequestUrl(API.manage_group.update, tenantId),
        args,
        config
    ).then((res) => res.data);
};

/**
 * 获取平台管理组信息
 */
export const getManageGroupInfo = (
    tenantId: string,
    args: {
        group_id: string;
    },
    config?: Parameters<typeof post>[2]
) => {
    return post<CorpGroupConfig>(getRequestUrl(API.manage_group.get, tenantId), args, config).then(
        (res) => res.data
    );
};

/** 导出登录统计数据 */
interface LoginStatsExportArgs extends LoginStatsQueryArgs {
    taskId?: string;
}
export const exportLoginStats = (
    tenantId: string,
    args: LoginStatsExportArgs,
    config?: Parameters<typeof post>[2]
) => {
    return post<{
        task_id: string | undefined;
    }>(getRequestUrl(API.audit.export_login_stats, tenantId), args, config).then((res) => res.data);
};

/** 获取客体企业概览 */
export interface CorpCoopOverview {
    has_corp_coop: boolean;
    corp_coop_quota: number;
    host_corp_list: {
        corp_id: string;
        name: string;
    }[];
    guest_corp_list: {
        corp_id: string;
        name: string;
        label_id_list: string[];
        status: DeptStatus;
    }[];
    label_list: {
        label_id: string;
        name: string;
    }[];
}
export const getCorpCoopOverview = (tenantId = authModel.tenantId) => {
    return post<CorpCoopOverview>(getRequestUrl(API.corp_coop.get_overview, tenantId)).then(
        (res) => res.data
    );
};

/** 撤销通讯录代管权限 */
export const revokeCorpCoopTmpOwner = (tenantId = authModel.tenantId) => {
    return post(getRequestUrl(API.corp_coop.host.revoke_tmp_owner, tenantId));
};
